import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { publicSupabase as supabase } from '../lib/supabase';
import Survey from './Survey';
import Feedback from './Feedback';
import PublicFormShell from '../components/public/PublicFormShell';

const darkenHex = (hex, f = 0.22) => {
  const m = /^#?([0-9a-f]{6})$/i.exec(hex || '');
  if (!m) return hex;
  const n = parseInt(m[1], 16);
  const d = (c) => Math.max(0, Math.round(c * (1 - f)));
  return `#${((d(n >> 16) << 16) | (d((n >> 8) & 255) << 8) | d(n & 255)).toString(16).padStart(6, '0')}`;
};

/**
 * The single front door for every public QR.
 *
 * Both /survey/:code and /feedback/:code land here. Previously each path had
 * its own page that re-implemented the same four steps — look the code up,
 * check expiry, count the scan, ask for the password — and each one filtered
 * on its own link_type, so a survey code opened under /feedback/ was reported
 * as a dead link. Printed QRs outlive the URL that was chosen for them, so
 * resolution is now by what the code IS, not by which path was scanned.
 *
 * The forms below are presentational: they receive a resolved qrLink (and,
 * for surveys, the template) and worry only about their own fields.
 */
export default function PublicForm() {
  const { code } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [netError, setNetError] = useState(false);
  const [qrLink, setQrLink] = useState(null);
  const [survey, setSurvey] = useState(null);

  const [needsPassword, setNeedsPassword] = useState(false);
  const [passwordInput, setPasswordInput] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [authenticated, setAuthenticated] = useState(false);

  const resolve = useCallback(async () => {
    setLoading(true);
    setNetError(false);
    setInvalid(false);
    try {
      // Deliberately NOT filtered by link_type — the code decides which form
      // it is, not the path it was reached through.
      const { data: qrData } = await supabase
        .from('qr_code_links')
        .select('*')
        .eq('code', code)
        .eq('is_active', true)
        .single();

      if (qrData) {
        if (qrData.expires_at && new Date(qrData.expires_at) < new Date()) {
          setInvalid(true);
          return;
        }
        setQrLink(qrData);

        if (qrData.password) setNeedsPassword(true);
        else setAuthenticated(true);

        // Via SECURITY DEFINER RPC: a direct UPDATE has no anon RLS policy and
        // silently updates zero rows.
        supabase.rpc('increment_qr_scan', { p_code: code }).then(({ error: scanErr }) => {
          if (scanErr) console.warn('scan count:', scanErr.message);
        });

        if (qrData.link_type === 'survey') {
          if (!qrData.target_id) { setInvalid(true); return; }
          const { data: surveyData, error } = await supabase
            .from('survey_templates')
            .select('*')
            .eq('id', qrData.target_id)
            .eq('is_active', true)
            .single();
          if (error || !surveyData) { setInvalid(true); return; }
          setSurvey(surveyData);
        }
        return;
      }

      // Not a QR code — a survey template opened by raw id. No password on
      // direct access, same as before.
      const { data: surveyData, error } = await supabase
        .from('survey_templates')
        .select('*')
        .eq('id', code)
        .eq('is_active', true)
        .single();

      if (error || !surveyData) { setInvalid(true); return; }
      setSurvey(surveyData);
      setAuthenticated(true);
    } catch (err) {
      // A blocked host or dead network is NOT "not found" — say so and let
      // them retry, rather than telling a guest their link is bad.
      console.error('Error resolving public code:', err);
      setNetError(true);
    } finally {
      setLoading(false);
    }
  }, [code]);

  useEffect(() => { resolve(); }, [resolve]);

  const handlePasswordSubmit = (e) => {
    e.preventDefault();
    if (passwordInput === qrLink?.password) {
      setAuthenticated(true);
      setNeedsPassword(false);
      setPasswordError('');
    } else {
      setPasswordError(t('publicForm.passwordIncorrect'));
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-brand-cream font-sans">
        <div className="text-center">
          <div className="mx-auto h-11 w-11 animate-spin rounded-full border-2 border-brand-peachLine border-t-brand-clay" />
          <p className="mt-4 text-brand-body">{t('publicForm.loading')}</p>
        </div>
      </div>
    );
  }

  if (netError) {
    return (
      <PublicFormShell title={t('publicForm.connErrorTitle')} narrow>
        <div className="rounded-3xl bg-white p-8 text-center shadow-[0_8px_30px_-12px_rgba(29,43,34,0.18)] ring-1 ring-brand-line">
          <div className="mb-4 text-5xl">📶</div>
          <p className="text-brand-body">{t('publicForm.connErrorBody')}</p>
          <button
            onClick={resolve}
            className="mt-6 rounded-full bg-brand-clay px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-clayDark"
          >
            {t('publicForm.retry')}
          </button>
        </div>
      </PublicFormShell>
    );
  }

  if (invalid) {
    return (
      <PublicFormShell title={t('publicForm.notFoundTitle')} narrow>
        <div className="rounded-3xl bg-white p-8 text-center shadow-[0_8px_30px_-12px_rgba(29,43,34,0.18)] ring-1 ring-brand-line">
          <div className="mb-4 text-5xl">🔗</div>
          <p className="text-brand-body">{t('publicForm.notFoundBody')}</p>
          <button
            onClick={() => navigate('/')}
            className="mt-6 rounded-full bg-brand-clay px-6 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-clayDark"
          >
            {t('publicForm.goHome')}
          </button>
        </div>
      </PublicFormShell>
    );
  }

  if (needsPassword && !authenticated) {
    return (
      <PublicFormShell title={t('publicForm.passwordTitle')} narrow>
        <div className="rounded-3xl bg-white p-8 shadow-[0_8px_30px_-12px_rgba(29,43,34,0.18)] ring-1 ring-brand-line">
          <div className="mb-6 text-center">
            <div className="mb-4 text-5xl">🔐</div>
            <p className="text-brand-body">{t('publicForm.passwordBody')}</p>
          </div>
          <form onSubmit={handlePasswordSubmit}>
            <input
              type="password"
              value={passwordInput}
              onChange={(e) => setPasswordInput(e.target.value)}
              className="mb-4 w-full rounded-xl border border-brand-line bg-white p-3 text-brand-ink placeholder:text-brand-body/60 focus:border-brand-clay focus:outline-none focus:ring-2 focus:ring-brand-clay/20"
              placeholder={t('publicForm.passwordPlaceholder')}
              autoFocus
            />
            {passwordError && <p className="mb-4 text-sm text-red-600">{passwordError}</p>}
            <button
              type="submit"
              className="w-full rounded-full py-3 font-semibold text-white transition-colors"
              style={{ background: survey?.theme_color ? `linear-gradient(to right, ${survey.theme_color}, ${darkenHex(survey.theme_color)})` : '#c0442d' }}
            >
              {t('publicForm.continue')}
            </button>
          </form>
        </div>
      </PublicFormShell>
    );
  }

  if (survey) return <Survey survey={survey} qrLink={qrLink} />;
  return <Feedback qrLink={qrLink} />;
}
